import { executeRecaptcha } from '@/lib/utils/recaptcha'
import { getAcquisitionContext, getMetaMatchKeys } from '@/lib/utils/acquisitionContext'

const API_URL = import.meta.env.VITE_PUBLIC_API_URL || 'http://localhost:3000'
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_PUBLIC_RECAPTCHA_SITE_KEY || ''

/** Made in the browser so the copy stays inside the click: an await first can lose clipboard access in Safari. */
export function newHandoffCode(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(32))
  const encoded = btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
  return `hbs_${encoded}`
}

/**
 * Registers a copied code in the background. Failure is fine: the agent gets the
 * verify link instead, so nothing here may interrupt the person.
 */
export async function registerHandoff(code: string, email: string): Promise<void> {
  try {
    const captchaToken = await executeRecaptcha(RECAPTCHA_SITE_KEY, 'agent_handoff')
    const { fbc, fbp } = getMetaMatchKeys()
    await fetch(`${API_URL}/api/v1/auth/agent-handoff`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({
        code,
        email: email || undefined,
        captchaToken,
        fbc,
        fbp,
        attribution: getAcquisitionContext(),
      }),
    })
  } catch {
    // The verify link still works.
  }
}

/** The session for an account an agent created from this browser's copied prompt, if there is one. */
export async function claimHandoff(signal?: AbortSignal): Promise<string | null> {
  try {
    const res = await fetch(`${API_URL}/api/v1/auth/agent-handoff/claim`, {
      method: 'POST',
      credentials: 'include',
      signal,
    })
    if (!res.ok) return null
    const data = (await res.json()) as { claimed?: boolean; authToken?: string }
    return data.claimed && data.authToken ? data.authToken : null
  } catch {
    return null
  }
}
